import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../Services/auth.service';
/*import { ThemeService, ThemeConfig } from '../../Services/theme.service';*/
import { AppConfigService, ThemeConfig } from '../../Services/appconfig.service';
import { LoginRequest } from '../../Models/Common/AuthModels';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Global } from '../../Shared/global';
import { AppContext } from '../../Shared/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApplicationConfiguration } from '../../Models/ApplicationConfiguration';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  showPassword = false;

  // Theme properties
  loginLeftBoxHtml: SafeHtml = '';
  loginLeftCssClass: string = 'hero';
  loginFooterHtml: SafeHtml = '';
  appTitle: string = 'FitSpeex';
  appDescription: string = '';
  logoUrl: string = '';

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private formBuilder: FormBuilder,
    private appctx: AppContext,
    private appConfigService: AppConfigService,
    private sanitizer: DomSanitizer
  ) {
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    // Redirect to dashboard if already logged in
    if (this.authService.isLoggedIn()) {
      this.router.navigate([Global.DefaultPage]);
      return;
    }

    // Load and apply theme configuration
    this.loadThemeConfiguration();

    // Subscribe to theme changes
    this.appConfigService.appConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe(appConfig => {
        this.applyThemeToComponent(appConfig.CustomSettings);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load theme configuration
   */
  private loadThemeConfiguration(): void {
    this.appConfigService.loadAppConfig().then(config => {
      this.applyThemeToComponent(config);

      if (!this.loginLeftBoxHtml || this.loginLeftCssClass === '') {
        this.setDefaultTheme();
      }
    }).catch(error => {
      Global.LogError('[LoginComponent] Error loading theme:', error);
      // Use defaults if theme loading fails
      this.setDefaultTheme();
    });
  }

  /**
   * Apply theme to component
   */
  private applyThemeToComponent(config: ApplicationConfiguration): void {
    if (!config || !config?.CustomSettings) {
      Global.Log('[LoginComponent] No custom settings found in config');
      this.setDefaultTheme();
      return;
    }
    if (config.CustomSettings.loginLeftBoxHtml) {
      this.loginLeftBoxHtml = this.sanitizer.bypassSecurityTrustHtml(config.CustomSettings.loginLeftBoxHtml);
    }

    if (config.CustomSettings.loginLeftCssClass) {
      this.loginLeftCssClass = config.CustomSettings.loginLeftCssClass;
    }

    if (config.CustomSettings.loginFooterHtml) {
      this.loginFooterHtml = this.sanitizer.bypassSecurityTrustHtml(config.CustomSettings.loginFooterHtml);
    }

    if (config.CustomSettings.appTitle) {
      this.appTitle = config.CustomSettings.appTitle;
    }

    if (config.CustomSettings.appDescription) {
      this.appDescription = config.CustomSettings.appDescription;
    }

    if (config.CustomSettings.logoUrl) {
      this.logoUrl = config.CustomSettings.logoUrl;
    }

    Global.Log('[LoginComponent] Theme applied');
  }

  /**
   * Set default theme if loading fails
   */
  private setDefaultTheme(): void {
    this.appTitle = 'FitSpeex';
    this.loginLeftCssClass = 'hero';
    this.loginLeftBoxHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<div class="hero-overlay"></div> <div class="logo"> <div class="logo-icon">✦</div> <span>Fitspeex</span> </div> <div class="hero-content"> <h1> Move <span>Your Way</span> </h1> <p> One platform for every kind of fitness. Gym, Yoga, Calisthenics and more. </p> </div> <!-- FITNESS MENU --> <div class="fitness-menu"> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">fitness_center</mat-icon> </div> <span>Gym</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">self_improvement</mat-icon> </div> <span>Yoga</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">sports_gymnastics</mat-icon> </div> <span>Calisthenics</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">directions_run</mat-icon> </div> <span>Cardio</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">more_horiz</mat-icon> </div> <span>More</span> </div> </div>`
    );
    this.loginFooterHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">grid_view</mat-icon> </div> <h3>All-in-One Fitness</h3> <p> Access gym, yoga, calisthenics, cardio and more in one place. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">person</mat-icon> </div> <h3>Personalized for You</h3> <p> Your goals, your plan. We adapt to you. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">analytics</mat-icon> </div> <h3>Track & Progress</h3> <p> Monitor your progress and stay motivated every day. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">verified_user</mat-icon> </div> <h3>Trusted & Secure</h3> <p> Your data is safe with us. Focus on what matters. </p> </div>`
    );
  }

  /**
   * Handle login
   */
  onLogin(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const credentials: LoginRequest = {
      username: this.loginForm.value.username,
      password: this.loginForm.value.password
    };

    this.authService.login(credentials)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success) {
            if (!response.organization || response.organization?.OrgId == 0) {
              this.router.navigate(['/org-selector']);
            } else {
              this.router.navigate([Global.DefaultPage]);
            }
          } else {
            this.errorMessage = response.message || 'Login failed. Please try again.';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.message || 'An error occurred during login. Please try again.';
        }
      });
  }

  /**
   * Toggle password visibility
   */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Get form control for template
   */
  get username() {
    return this.loginForm.get('username');
  }

  get password() {
    return this.loginForm.get('password');
  }
}
